import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, AUTH_EXPIRED_EVENT } from '../services/api';
import { clearSessionTokens, getAccessToken, getCenterId, getRefreshToken, getRoleName, migrateLegacySession, setCenterId, setRoleName, setSessionTokens } from '../utils/sessionStorage';
import { claimUserForTab, releaseUserForTab } from '../utils/tabSessionGuard';
import { deactivateWebPushDevice, initializeAndroidPush, initializeWebPushIfGranted } from '../platform/pushMessaging';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const restoreSession = async () => {
      migrateLegacySession();
      if (!getAccessToken() && !getRefreshToken()) {
        setLoading(false);
        return;
      }
      try {
        let profile;
        try {
          profile = await api.auth.me();
        } catch (error) {
          if (error.status !== 403) throw error;
          setCenterId(null);
          profile = await api.auth.me();
        }
        const tabAvailable = await claimUserForTab(profile.id);
        if (!tabAvailable) {
          clearSessionTokens();
          window.sessionStorage?.setItem('pulsora_session_notice', 'Este usuario ya está abierto en otra pestaña de este navegador.');
          setUser(null);
          return;
        }
        if (profile.active_center?.centro_id && !getCenterId()) setCenterId(profile.active_center.centro_id);
        if (profile.role && profile.role !== getRoleName()) setRoleName(profile.role);
        setUser(profile);
      } catch (error) {
        if (error.status === 401) clearSessionTokens();
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);


  useEffect(() => {
    if (!user?.id) return undefined;

    initializeAndroidPush().catch(error => {
      console.error(
        'PULSORA_PUSH_INIT_FAILED:',
        error?.message || error
      );
    });

    initializeWebPushIfGranted().catch(error => {
      console.error(
        'PULSORA_WEB_PUSH_INIT_FAILED:',
        error?.message || error
      );
    });

    return undefined;
  }, [user?.id]);

  useEffect(() => {
    const handleExpiredSession = () => { releaseUserForTab(); setUser(null); };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpiredSession);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpiredSession);
  }, []);


  useEffect(() => {
    if (!user?.id) return undefined;

    // Mantiene viva la unica sesion mientras esta ventana de Pulsora siga
    // abierta. Al cerrar el navegador deja de enviarse el heartbeat y el
    // backend libera la sesion automaticamente tras unos minutos.
    const heartbeat = () => {
      if (document.visibilityState === 'hidden') return;
      api.auth.heartbeat().catch(() => null);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') heartbeat();
    };

    heartbeat();
    const intervalId = window.setInterval(heartbeat, 60 * 1000);
    window.addEventListener('focus', heartbeat);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', heartbeat);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.id]);

  const establishAuthenticatedSession = useCallback(async tokens => {
    // Una nueva autenticación no debe heredar centro ni rol de una sesión previa.
    setCenterId(null);
    setRoleName(null);
    setSessionTokens(tokens);
    const profile = await api.auth.me();
    const tabAvailable = await claimUserForTab(profile.id);
    if (!tabAvailable) {
      clearSessionTokens();
      const duplicate = new Error('Este usuario ya está abierto en otra pestaña de este navegador.');
      duplicate.status = 409;
      duplicate.data = { code: 'ACTIVE_SESSION_EXISTS' };
      throw duplicate;
    }
    if (profile.active_center?.centro_id) setCenterId(profile.active_center.centro_id);
    if (profile.role) setRoleName(profile.role);
    setUser(profile);
    return profile;
  }, []);

  const login = useCallback(async (username, password, forceSession = false) => {
    const tokens = await api.auth.login(username.trim(), password, forceSession);
    return establishAuthenticatedSession(tokens);
  }, [establishAuthenticatedSession]);

  const googleLogin = useCallback(async (credential, forceSession = false) => {
    const tokens = await api.auth.googleLogin(credential, forceSession);
    const profile = await establishAuthenticatedSession(tokens);
    return { profile, createdAccount: Boolean(tokens.created_account) };
  }, [establishAuthenticatedSession]);

  const appleLogin = useCallback(async (
    credential,
    nonce,
    accountData = {},
    forceSession = false,
  ) => {
    const tokens = await api.auth.appleLogin(
      credential,
      nonce,
      accountData,
      forceSession,
    );

    const authenticatedProfile =
      await establishAuthenticatedSession(tokens);

    return {
      profile: authenticatedProfile,
      createdAccount: Boolean(tokens.created_account),
    };
  }, [establishAuthenticatedSession]);

  const logout = async () => {
    // PULSORA WEB PUSH V1: desactiva el token de este navegador antes de
    // borrar las credenciales locales. En el proximo login se reactiva.
    await deactivateWebPushDevice().catch(() => null);
    const remoteLogout = api.auth.logout().catch(() => null);
    releaseUserForTab();
    clearSessionTokens();
    setUser(null);
    await remoteLogout;
  };

  const selectCenter = async centerId => {
    const previousCenterId = getCenterId();
    const previousRole = getRoleName();
    setCenterId(centerId);
    setRoleName(null);
    try {
      const profile = await api.auth.me();
      if (profile.role) setRoleName(profile.role);
      setUser(profile);
      return profile;
    } catch (error) {
      setCenterId(previousCenterId);
      setRoleName(previousRole);
      throw error;
    }
  };

  const selectRole = async role => {
    const previousRole = getRoleName();
    setRoleName(role);
    try {
      const profile = await api.auth.me();
      setUser(profile);
      return profile;
    } catch (error) {
      setRoleName(previousRole);
      throw error;
    }
  };

  const refreshUser = async () => {
    const profile = await api.auth.me();
    if (profile.role) setRoleName(profile.role);
    setUser(profile);
    return profile;
  };
  const resetCenterContext = useCallback(async () => {
    setCenterId(null);
    setRoleName(null);
    const profile = await api.auth.me();
    if (profile.active_center?.centro_id) setCenterId(profile.active_center.centro_id);
    if (profile.role) setRoleName(profile.role);
    setUser(profile);
    return profile;
  }, []);

  const deleteAccount = useCallback(async confirmation => {
    await api.auth.deleteAccount(confirmation);
    releaseUserForTab();
    clearSessionTokens();
    setCenterId(null);
    setRoleName(null);
    setUser(null);
  }, []);

  const updateProfilePhoto = useCallback(profilePhoto => setUser(current => {
    const nextPhoto = profilePhoto || null;
    if (!current || current.profile_photo === nextPhoto) return current;
    return { ...current, profile_photo: nextPhoto };
  }), []);
  const value = {
    user, loading, login, googleLogin, appleLogin, logout, selectCenter, selectRole, refreshUser,
    resetCenterContext, deleteAccount, updateProfilePhoto,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
